import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

export const EmailCertificateDialog = ({ inv, onClose, onSent }) => {
  const [clientEmail, setClientEmail] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => { setClientEmail(inv?.client_email || ""); }, [inv]);

  const send = async (recipient) => {
    setBusy(recipient);
    try {
      if (recipient === "client") {
        const value = clientEmail.trim();
        if (!value) { toast.error("Enter the client's email"); return; }
        if (value.toLowerCase() !== (inv.client_email || "")) {
          await api.put(`/invitations/${inv.id}/client-email`, { client_email: value });
          onSent();
        }
      }
      const r = await api.post(`/invitations/${inv.id}/send-certificate`, { recipient });
      toast.success(`Certificate emailed to ${r.data.sent_to}`);
      onSent();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not send certificate"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={!!inv} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="email-certificate-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black tracking-tighter">Email certificate</DialogTitle>
          <DialogDescription>
            Sends a secure link to download {inv?.candidate_name}'s PDF certificate for {inv?.course_title}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-3 border border-border rounded-sm p-3">
            <div className="text-sm min-w-0">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">Candidate</div>
              <div className="truncate" data-testid="email-cert-candidate-address">{inv?.candidate_email}</div>
            </div>
            <Button variant="outline" onClick={() => send("candidate")} disabled={!!busy}
              data-testid="email-cert-send-candidate" className="rounded-sm shrink-0">
              <Mail size={14} className="mr-2" /> {busy === "candidate" ? "Sending…" : "Send"}
            </Button>
          </div>
          <div>
            <Label className="text-xs font-bold tracking-[0.2em] uppercase">Client email</Label>
            <div className="mt-2 flex gap-2">
              <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)}
                placeholder="manager@nursery.co.uk" data-testid="email-cert-client-input" className="rounded-sm h-11" />
              <Button onClick={() => send("client")} disabled={!!busy} data-testid="email-cert-send-client"
                className="rounded-sm h-11 bg-foreground text-background hover:bg-foreground/90 shrink-0">
                <Send size={14} className="mr-2" /> {busy === "client" ? "Sending…" : "Send to client"}
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Saved on this candidate for next time.</p>
          </div>
          {inv?.certificate_sent_at && (
            <p className="font-mono text-[10px] text-muted-foreground" data-testid="email-cert-last-sent">
              Last emailed: {new Date(inv.certificate_sent_at).toLocaleString()}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
